import axios from 'axios'

// Use environment variable for API URL, fallback to relative path for production
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api'

// Create axios instance
export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

let refreshPromise: Promise<string> | null = null

const refreshAccessToken = async (): Promise<string> => {
  const refresh = localStorage.getItem('auth-refresh-token')
  if (!refresh) throw new Error('No refresh token available')

  const response = await axios.post(`${API_BASE_URL}/auth/token/refresh/`, { refresh })
  const access = response.data.access as string
  localStorage.setItem('auth-token', access)
  if (response.data.refresh) {
    localStorage.setItem('auth-refresh-token', response.data.refresh)
  }
  return access
}

// Asking for and using a password reset link happens signed out, so an old
// token left in the browser must not be sent (an expired one would be refused).
const PUBLIC_AUTH_PATHS = ['/auth/forgot-password/', '/auth/reset-password/']
const isPublicAuthRequest = (url?: string) => PUBLIC_AUTH_PATHS.some((path) => url?.includes(path))

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth-token')
    if (token && !isPublicAuthRequest(config.url)) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config
    const isAuthRequest = originalRequest?.url?.includes('/auth/login/') ||
      originalRequest?.url?.includes('/auth/token/refresh/') ||
      originalRequest?.url?.includes('/auth/logout/') ||
      isPublicAuthRequest(originalRequest?.url)

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry && !isAuthRequest) {
      originalRequest._retry = true
      try {
        refreshPromise ??= refreshAccessToken().finally(() => {
          refreshPromise = null
        })
        const access = await refreshPromise
        originalRequest.headers.Authorization = `Bearer ${access}`
        return api(originalRequest)
      } catch {
        localStorage.removeItem('auth-token')
        localStorage.removeItem('auth-refresh-token')
        localStorage.removeItem('auth-storage')
        if (window.location.pathname !== '/login') window.location.href = '/login'
      }
    }
    return Promise.reject(error)
  }
)

export default api
